import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getActorDisplayName } from '../../utils/roleUtils';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import PermissionChecklist from '../shared/PermissionChecklist';
import type { RoleOption } from '../../services/api/onboardingService';
import type { UserRow } from './tables/userManagementTypes';
import {
    getDeptPermissionTemplates,
    type PermissionTemplate,
} from '../../services/api/departmentPermissionService';
import PERMISSION_KEYS from '../../constants/permission-keys';
import {
    getPermissionLabel,
    PERMISSION_DESCRIPTION_KEYS,
} from '../../constants/permission-meta';

const ALL_PERMISSIONS = Object.values(PERMISSION_KEYS) as string[];

export interface EditFacilityUserValues {
    roleId: string;
    expiryDate: string;
    permissions: string[];
}

interface EditFacilityUserDialogProps {
    open: boolean;
    user: UserRow | null;
    roles: RoleOption[];
    /** Set when viewing another department as NI admin. */
    departmentId?: string;
    onOpenChange: (open: boolean) => void;
    onSave: (values: EditFacilityUserValues) => Promise<void>;
}

export default function EditFacilityUserDialog({
    open,
    user,
    roles,
    departmentId,
    onOpenChange,
    onSave,
}: EditFacilityUserDialogProps) {
    const { t } = useTranslation();

    // Same option list as the department role edit drawer, with the
    // description as a tooltip instead of inline text.
    const permissionSections = useMemo(
        () => [
            {
                items: ALL_PERMISSIONS.map((key) => {
                    const descKey = PERMISSION_DESCRIPTION_KEYS[key];
                    return {
                        key,
                        label: getPermissionLabel(key, t),
                        tooltip: descKey ? t(descKey) : undefined,
                    };
                }),
            },
        ],
        [t]
    );

    // Users can only be assigned department-scoped roles here.
    const resolvedRoles = useMemo(
        () => roles.filter((role) => role.scope === 'department'),
        [roles]
    );

    const initialRoleId = useMemo(() => {
        if (!user) return '';
        return (
            resolvedRoles.find(
                (role) =>
                    role._id === user.roleId ||
                    role.role.trim().toLowerCase() ===
                        user.role.trim().toLowerCase()
            )?._id || ''
        );
    }, [resolvedRoles, user]);
    const [roleId, setRoleId] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [permissions, setPermissions] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const [templates, setTemplates] = useState<PermissionTemplate[]>([]);

    useEffect(() => {
        if (!user) return;
        setRoleId(initialRoleId);
        setExpiryDate(user.expiryDate ? user.expiryDate.slice(0, 10) : '');
        setPermissions(user.deptPermissions ?? []);
    }, [initialRoleId, user]);

    useEffect(() => {
        if (!open) return;
        getDeptPermissionTemplates(departmentId)
            .then(setTemplates)
            .catch(() => setTemplates([]));
    }, [open, departmentId]);

    // Selecting a role rebuilds the selection from the user's SAVED
    // permissions plus the new role's template — so template permissions
    // auto-added by a previously selected role don't linger, while nothing
    // the user actually has is ever removed.
    const savedPermissions = useMemo(
        () => user?.deptPermissions ?? [],
        [user]
    );
    const handleRoleChange = (nextRoleId: string) => {
        setRoleId(nextRoleId);
        const rolePermissions =
            templates.find((tmpl) => tmpl.actor_id === nextRoleId)
                ?.deptPermissions ?? [];
        setPermissions([
            ...savedPermissions,
            ...rolePermissions.filter((p) => !savedPermissions.includes(p)),
        ]);
    };

    const handleSave = async () => {
        if (!roleId || !expiryDate) return;

        setSaving(true);
        try {
            await onSave({ roleId, expiryDate, permissions });
            onOpenChange(false);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{t('facilities.editUser.title')}</DialogTitle>
                    <DialogDescription>
                        {user
                            ? t('facilities.editUser.descriptionWithEmail', { email: user.email })
                            : t('facilities.editUser.descriptionFallback')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="space-y-1.5">
                        <Label>{t('facilities.editUser.role')}</Label>
                        <Select value={roleId} onValueChange={handleRoleChange}>
                            <SelectTrigger className="h-9">
                                <SelectValue placeholder={t('facilities.editUser.selectRole')} />
                            </SelectTrigger>
                            <SelectContent>
                                {resolvedRoles.map((role) => (
                                    <SelectItem key={role._id} value={role._id}>
                                        {getActorDisplayName(role)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="facility-user-expiry-date">
                            {t('facilities.editUser.expiryDate')}
                        </Label>
                        <Input
                            id="facility-user-expiry-date"
                            type="date"
                            value={expiryDate}
                            onChange={(e) => setExpiryDate(e.target.value)}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>{t('facilities.editUser.permissions')}</Label>
                        <PermissionChecklist
                            sections={permissionSections}
                            selected={permissions}
                            onChange={setPermissions}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={saving}
                    >
                        {t('facilities.editUser.cancel')}
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={!roleId || !expiryDate || saving}
                    >
                        {saving ? t('facilities.editUser.saving') : t('facilities.editUser.save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
